import { useQuery } from "react-query";
import useApiService from "../utilities/useApiService";

const fetchStringList = async (
  getMethod: ReturnType<typeof useApiService>["getMethod"],
  path: string,
): Promise<string[]> => {
  const res = await getMethod<string[]>(path);
  return Array.isArray(res.data) ? res.data : [];
};

export const MEDISPAN_DOSAGE_OPTIONS_KEY = ["/drugs/options/dosages"] as const;
export const MEDISPAN_PACKAGE_UOM_OPTIONS_KEY = ["/drugs/options/uoms"] as const;
export const MEDISPAN_STRENGTH_UOM_OPTIONS_KEY = [
  "/drugs/options/strength-uoms",
] as const;

export function useMedispanDrugFormOptions(enabled = true) {
  const { getMethod } = useApiService();

  const { data: dosageOptions = [], isLoading: dosagesLoading } = useQuery(
    MEDISPAN_DOSAGE_OPTIONS_KEY,
    () => fetchStringList(getMethod, "/drugs/options/dosages"),
    { enabled, staleTime: 5 * 60 * 1000 },
  );

  const { data: packageUomOptions = [], isLoading: packageUomsLoading } =
    useQuery(
      MEDISPAN_PACKAGE_UOM_OPTIONS_KEY,
      () => fetchStringList(getMethod, "/drugs/options/uoms"),
      { enabled, staleTime: 5 * 60 * 1000 },
    );

  const { data: strengthUomOptions = [], isLoading: strengthUomsLoading } =
    useQuery(
      MEDISPAN_STRENGTH_UOM_OPTIONS_KEY,
      () => fetchStringList(getMethod, "/drugs/options/strength-uoms"),
      { enabled, staleTime: 5 * 60 * 1000 },
    );

  return {
    dosageOptions,
    packageUomOptions,
    strengthUomOptions,
    isLoading: dosagesLoading || packageUomsLoading || strengthUomsLoading,
  };
}

/** Keep the current saved value visible even if it is not in MediSpan distinct lists. */
export function withCurrentSelectOption(
  options: string[],
  current?: string | null,
): string[] {
  const value = current?.trim();
  if (!value || options.includes(value)) {
    return options;
  }
  return [...options, value].sort((a, b) => a.localeCompare(b));
}
